import { useState, useEffect, useRef, useCallback } from 'react';
import { postStakeFee, postStake } from '@/services/stake';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { GasFeeInfo } from '@/types/common';
import BigNumber from 'bignumber.js';
import { StakeOption, Process } from '@/types/stake';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { ethAddress } from '@/config/address';
import { FeeLevel } from '@/types/common';
import { debounce } from 'lodash-es';
import { useToast } from '@web3/base-hooks';
import { useWallet } from '@/hooks/wallet';
import { useFm } from '@web3/base-hooks';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import {
  getButtonIsHidden,
  getBtnEmergencyMode,
} from '@/containers/operation/utils';
import { PoolButtonType } from '@/types/pool';
import { SupportWallets } from '@/types/wallet';
import { PostStakeFeeParams, PostStakeParams } from '@/types/stake';
import { getStakeEstimateToken } from '@/services/stake';
import { CoinInfo } from '@/types/common';

/**
 * gas费相关hook
 */
export const useGasFee = (option: Partial<StakeOption>) => {
  const { stakeAmount, currentStakeToken, aprPerDay, process } =
    option as StakeOption;
  const [stakeLpInfo, setStakeLpInfo] = useState({} as Partial<CoinInfo>);

  const [gasFeeInfo, setGasFeeInfo] = useState<Partial<GasFeeInfo>>({});
  const [gasFeeLevel, setGasFeeLevel] = useState(FeeLevel.Middle);
  const [loopPercent, setLoopPercent] = useState(0);
  const [gasFeeIsLoading, setGasFeeIsLoading] = useState(false);
  const { poolInfo } = usePoolInfoContext();
  const { coinInfoStore } = useCoinInfoContext();
  const { toastWarn } = useToast();
  const { isLogin } = useWallet();

  const selectedGasFeeInfo = gasFeeInfo?.[gasFeeLevel];
  const gasToken = coinInfoStore?.[`${poolInfo.chainCode}${ethAddress}`];
  const gasFeePrice = BigNumber(selectedGasFeeInfo?.gasFeeMax || 0)
    .times(gasToken?.price)
    .toString();
  const gasStore = {
    hasGasFee: !!selectedGasFeeInfo?.gasFeeMax,
    level: gasFeeLevel,
    amount: selectedGasFeeInfo?.gasFeeMax || '0',
    price: gasFeePrice,
    gasPrice: selectedGasFeeInfo?.gasPrice || '0',
    max: gasFeeInfo?.gasMax || '0',
    estimateFee: selectedGasFeeInfo?.gasFee || '0',
    priority: selectedGasFeeInfo?.priority || '0',
    symbol: gasToken?.symbol || '',
    isLoading: gasFeeIsLoading,
    limit: gasFeeInfo?.gasLimit || '',
    loopPercent: loopPercent * 20,
    isGreatThanProfit: BigNumber(aprPerDay)
      .times(30)
      .lt(gasFeePrice || '0'),
  };

  const timer = useRef<NodeJS.Timeout>();
  const uid = useRef(0);
  const currentProcess = useRef(process);

  const fetchGasFee = async (
    selfUid: number,
    selfStakeAmount: string,
    selfContractAddress: string
  ) => {
    setGasFeeIsLoading(true);
    // 未登录时，不请求预估接口
    if (!isLogin) {
      setGasFeeIsLoading(false);
      return;
    }
    const params: PostStakeFeeParams = {
      poolId: poolInfo.id,
      chainCode: poolInfo.chainCode,
      poolCode: poolInfo.poolCode,
      poolType: poolInfo.poolType,
      amounts: [
        {
          amount: BigNumber(selfStakeAmount || 0).toString(),
          contractAddress: selfContractAddress,
        },
      ],
      // 写死0.5为了通接口，无意义
      slippage: 0.5,
      level: FeeLevel.Middle,
    };

    if (poolInfo.chainCode === 'SOL') {
      // 小于最小质押金额时，不请求接口
      const { min } = poolInfo?.poolExtendDTO?.stakeRange || {};
      if (min && BigNumber(selfStakeAmount || 0).lt(min)) {
        return;
      }
      const { result: estimateResult } = await getStakeEstimateToken({
        poolId: poolInfo.id,
        slippage: '',
        stakeTokens: [
          {
            amount: selfStakeAmount,
            contractAddress: selfContractAddress,
          },
        ],
      });
      params.minOutAmount = estimateResult.lpAmount;
      setStakeLpInfo({
        ...estimateResult?.lpCoinDetail,
        amount: estimateResult?.lpAmount,
        router: estimateResult.router ?? '',
        minOutAmount: estimateResult.lpAmount,
      });
    }

    const { success, result, message } = await postStakeFee(params);
    if (selfUid !== uid.current) return;

    setGasFeeIsLoading(false);

    if (!success) {
      toastWarn(message);
      return false;
    }

    setGasFeeInfo(result.gas);

    return true;
  };

  // gas轮询，2s一次，0.5s的debounce buffer
  const startLoopGasFee = useCallback(
    debounce(
      async (
        percent = 0,
        selfStakeAmount: string,
        selfContractAddress: string
      ) => {
        clearTimeout(timer.current);
        if (
          BigNumber(selfStakeAmount).isNaN() ||
          BigNumber(selfStakeAmount).eq(0)
        )
          return;

        setLoopPercent(percent);

        if (percent === 0) {
          uid.current = Math.random();
          const result = await fetchGasFee(
            uid.current,
            selfStakeAmount,
            selfContractAddress
          );
          // 报错则取消轮循
          if (!result) return;
        }

        // 只有在确认弹窗的时候，再轮训gas fee
        if (currentProcess.current !== Process.Confirm) {
          return;
        }

        timer.current = setTimeout(() => {
          if (percent === 4) {
            setLoopPercent(5);
            startLoopGasFee(0, selfStakeAmount, selfContractAddress);
          } else {
            startLoopGasFee(percent + 1, selfStakeAmount, selfContractAddress);
          }
        }, 2000);
      },
      500
    ),
    []
  );

  const onChangeGasFeeLevel = (gasFeeLevel: FeeLevel) => {
    setGasFeeLevel(gasFeeLevel);
  };

  // 每次更改输入框则清空gasfee
  useEffect(() => {
    setGasFeeInfo({});
  }, [stakeAmount, currentStakeToken.contractAddress, startLoopGasFee]);

  useEffect(() => {
    currentProcess.current = process;
    // 开启质押流程后终止gasfee轮询
    if (process !== Process.None && process !== Process.Confirm) {
      clearTimeout(timer.current);
      return;
    }

    startLoopGasFee(0, stakeAmount, currentStakeToken.contractAddress);
    return () => {
      clearTimeout(timer.current);
      uid.current = 0;
    };
  }, [
    stakeAmount,
    currentStakeToken.contractAddress,
    startLoopGasFee,
    process,
  ]);

  return {
    gasStore,
    gasFeeInfo,
    stakeLpInfo,
    onChangeGasFeeLevel,
  };
};

/**
 * 开始质押相关hook
 */
export const useStake = (option: Partial<StakeOption>) => {
  const {
    stakeAmount,
    currentStakeToken,
    slippage,
    gasStore,
    stakeLpInfo,
    onChangeProcess,
  } = option as StakeOption;
  const { poolInfo } = usePoolInfoContext();
  const [confirmButtonIsLoading, setConfirmButtonIsLoading] = useState(false);
  const [orderCode, setOrderCode] = useState('');
  const { toastWarn } = useToast();

  const beginStake = async () => {
    setConfirmButtonIsLoading(true);

    const params: PostStakeParams = {
      poolId: poolInfo.id,
      chainCode: poolInfo.chainCode,
      poolCode: poolInfo.poolCode,
      poolType: poolInfo.poolType,
      amounts: [
        {
          amount: BigNumber(stakeAmount || 0).toString(),
          contractAddress: currentStakeToken.contractAddress,
        },
      ],
      slippage,
      estimateFee: [BigNumber(gasStore.estimateFee).toNumber()],
      baseFee: BigNumber(gasStore.gasPrice).toNumber(),
      gasLimit: BigNumber(gasStore.limit).toNumber(),
      priority: BigNumber(gasStore.priority).toNumber(),
      gasMax: BigNumber(gasStore.max).toNumber(),
      maxPriorityFeePerGas: BigNumber(gasStore.priority).toNumber(),
      minOutAmount: stakeLpInfo?.minOutAmount,
      router: stakeLpInfo?.router ?? '',
    };

    const { success, result, message } = await postStake(params);

    setConfirmButtonIsLoading(false);

    if (!success) {
      toastWarn(message);
      return;
    }

    if (success) {
      onChangeProcess(Process.Result);
      setOrderCode(result.orderCode);
    }
  };

  return {
    orderCode,
    confirmButtonIsLoading,
    beginStake,
  };
};

/**
 * 当前按钮相关状态
 */
export const useButtonStatus = (option: Partial<StakeOption>) => {
  const { stakeAmount, gasStore, onChangeProcess } = option as StakeOption;
  const { isLogin, openLoginModal, walletAddresses } = useWallet();
  const { poolInfo } = usePoolInfoContext();
  const [textError, setTextError] = useState('');
  const fm = useFm();

  const cloudAddress =
    walletAddresses?.find((item) => item.chainId === poolInfo.chainId)
      ?.walletAddress ?? '';

  const stakeIsHidden = getButtonIsHidden(
    PoolButtonType.Stake,
    poolInfo.poolExtendDTO
  );

  const stakeEmergencyDisable = getBtnEmergencyMode(
    PoolButtonType.Stake,
    poolInfo.poolExtendDTO,
    cloudAddress
  );

  const onChangeTextError = (v: string) => {
    setTextError(v);
  };

  const buttonDisabled = (() => {
    if (!isLogin) {
      return false;
    }

    if (
      poolInfo.supportWallets.length &&
      !poolInfo.supportWallets.includes(SupportWallets.bybitWallet)
    ) {
      return true;
    }

    if (BigNumber(stakeAmount || 0).lte(0)) {
      return true;
    }

    if (!gasStore.hasGasFee) {
      return true;
    }

    if (textError?.length) {
      return true;
    }
  })();

  const buttonText = (() => {
    if (!isLogin) {
      return fm('connect_wallet');
    }

    if (
      poolInfo.supportWallets.length &&
      !poolInfo.supportWallets.includes(SupportWallets.bybitWallet)
    ) {
      return fm('onlySupportMnemonicWallet');
    }

    return fm('stakeNow1');
  })();

  const onClickStake = () => {
    if (!isLogin) {
      openLoginModal();
      return;
    }

    scTrack(Event.Web3UserClick, {
      page_name: 'StakingWeb_bbSOLPagestakeCloud_start',
      button_name: 'Stake',
    });

    if (gasStore.isGreatThanProfit) {
      onChangeProcess(Process.GasFeeTip);
      return;
    }

    onChangeProcess(Process.Confirm);
  };

  return {
    stakeEmergencyDisable,
    stakeIsHidden,
    buttonDisabled,
    buttonText,
    textError,
    onClickStake,
    onChangeTextError,
  };
};
